import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router, ActivatedRoute } from '@angular/router';
import { jwtDecode } from 'jwt-decode';
import { Subject, Observable, of, combineLatest, from, BehaviorSubject, throwError } from 'rxjs';
import { takeUntil, switchMap, map, catchError, take } from 'rxjs/operators';
import { AppContextService, EcaSetupConfigurationService, SetupConfigAttribute, EcaGenericMessageDialogComponent, EcaGenericMessageDialogConfig, EcaPillarBoxModule, EcaFormColumnModule, EcaDrcInsightLogoModule, EcaSpinner } from '@drc-eca/eca-components-lib';
import { PortalError } from '../../shared/portal-error.model';
import { UserSecurityService } from '../../user/services/user-security.service';
import { NewPasswordComponent } from '../new-password/new-password.component';
import { DefaultLayoutDirective, DefaultLayoutAlignDirective } from '@ngbracket/ngx-layout/flex';
import { AsyncPipe } from '@angular/common';

@Component({
    selector: 'app-activate-account',
    templateUrl: './activate-account.component.html',
    styleUrls: ['./activate-account.component.scss'],
    imports: [
      NewPasswordComponent,
      EcaPillarBoxModule,
      EcaFormColumnModule,
      EcaDrcInsightLogoModule,
      DefaultLayoutDirective,
      DefaultLayoutAlignDirective,
      AsyncPipe,
      EcaSpinner
    ]
})
export class ActivateAccountComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private userSecurityService = inject(UserSecurityService);
  private appContextService = inject(AppContextService);
  private setupConfigService = inject(EcaSetupConfigurationService);
  dialog = inject(MatDialog);


  showResendLink = false;
  productCode: string;
  productCode$: Observable<string>;
  temporaryToken: string;
  destroyed = new Subject<boolean>();
  attributes$: Observable<object>;
  useOkta$: Observable<boolean>;
  userId: string;
  isLoading = new BehaviorSubject(true);
  isLoading$ = this.isLoading.asObservable();
  islandDisabled = false;
  form = {
    status: 200,
    show: true
  };

  ngOnInit() {
    this.productCode$ = this.activatedRoute.queryParams.pipe(
      takeUntil(this.destroyed),
      map((params) => {
        this.productCode = params['productCode'];
        if (this.productCode) {
          this.appContextService.setSelectedProductCode(this.productCode);
        }
        this.temporaryToken = params['token'].replace(/(\r\n|\n|\r)/gm, '');
        this.userId = (jwtDecode(this.temporaryToken) as any).sub;
        return params['productCode'];
      }));

    this.productCode$.subscribe();

    this.attributes$ = this.productCode$.pipe(
      switchMap(productCode => {
        if (productCode) {
          return this.setupConfigService.getAttributesForClient(productCode);
        } else {
          return of([{
            attribute: {
              key: 'login-method'
            },
            value: 'eca'
          }] as SetupConfigAttribute[]);
        }
      }),

      // Map array of attribute objects to a HashMap of key=value
      map(attrs => attrs.reduce((acc, attr) => (acc[attr.attribute.key] = attr.value, acc), {})),
      catchError((error) => {
        if (error.status === 404) {
          this.router.navigate(['not-found']);
          return of({});
        }
        return of({});
      })
    );

    this.useOkta$ = this.attributes$.pipe(
      map(attributes => attributes['login-method'] === 'okta')
    );
  }

  openGoodModal(inMessage: string) {
    const data: EcaGenericMessageDialogConfig = {
      title: 'Success',
      message: `<p>${inMessage}</p>`,
    };
    this.dialog.open(EcaGenericMessageDialogComponent, { data });
  }

  openBadModal(err) {
    const data: EcaGenericMessageDialogConfig = {
      title: 'Password Not Accepted',
      icon: 'warning-triangle',
      message: `<p>${err}</p>`,
    };
    this.dialog.open(EcaGenericMessageDialogComponent, { data });
  }

  openDialog(config: EcaGenericMessageDialogConfig) {
    return this.dialog.open(EcaGenericMessageDialogComponent, {
      data: config,
      width: '600px',
      panelClass: 'activate-account-dialog'
    });
  }

  saveProfile(newPassword) {
    const pswdObject = {
      password: newPassword,
      token: this.temporaryToken
    };

    combineLatest([this.useOkta$, this.userSecurityService.activateAccount(this.userId, pswdObject), this.productCode$]).pipe(
      switchMap(([useOkta, user, prdCode]) => {
        if (useOkta) {
          this.appContextService.setToken(null as any);
          if (!prdCode) {
            return from(this.router.navigate(['/login']));
          } else {
            return from(this.router.navigate([`/login/${prdCode}`]));
          }
        } else {
          this.appContextService.setToken(user.token);
          return this.userSecurityService.getUserForToken(user.token).pipe(
            map((token) => {
              // Even though we don't do anything with the getUserForToken response, it has side effects we need to login the user
              this.loading(false);
              // this.openGoodModal('Your account has been activated. Please login to continue.');
              this.router.navigate(['/login']);
              return token;
            }),
            catchError((e) => {
              if (e.error?.code === 'island') {
                this.openGoodModal('Your account has been activated. Please login through the Island Browser.');
                this.islandDisabled = true;
                return of(true);
              }
              return throwError(e);
            })
          );
        }
      })
    ).subscribe(
      () => {
        this.loading(false);
      },
      (err: PortalError) => {
        let message: string;
        this.loading(false);
        if (err.status === 400) {
          message = `<br/>There was a problem activating your account. Please try again. <br/><br/>It is possible the password you have provided does not meet the criteria for a new password.
          Remember, passwords may not contain your first or last name or your username. In addition, try adding at least one of these characters to the middle of the password:
          !#$%&()*+,-./:;<=>?@[]^_\`{|} ~<br/><br/>`;

          this.openBadModal(message)
        } else {
          switch (err.status) {
            case 403: {
              message = 'Your account activation invitation has expired. For a new account activation link, click on Send a New Link.';
              break;
            }
            case 409: {
              message = 'This account has already been activated. If you need to reset your password, please <a href="#/forgot-password">click here</a> to reset your password.';
              break;
            }
            default: {
              const friendlyMessage = err.friendlyErrorMessage || 'There was a problem activating your account.';
              message = `${friendlyMessage} Please request a new activation email.`;
            }
          }

          this.showResendLink = true;
          this.form.status = err.status;
          if (err.status === 403) {
            const data: EcaGenericMessageDialogConfig = {
              message: message,
              title: 'Expired Account Activation',
              icon: 'warning-triangle',
              buttons: {
                negative: {
                  label: 'Cancel'
                },
                positive: {
                  label: 'Send a New Link'
                }
              }
            };

            this.openDialog(data).beforeClosed()
              .pipe(take(1))
              .subscribe(val => {
                if (val) {
                  this.resendEmail();
                }
              });
          } else {
            this.openBadModal(message);
          }
        }
      }
    );
  }

  resendEmail() {
    this.loading(true);
    this.userSecurityService.sendActivateAccountEmail(this.productCode, this.temporaryToken).subscribe(
      () => {
        this.form.show = false;
        this.loading(false);
      },
      (err) => {
        this.loading(false);
        let message = '';
        const friendlyMessage = err.friendlyErrorMessage || 'There was a problem activating your account.';
        message = `${friendlyMessage} Please request a new activation email.`;
        if (err.status === 403) {
          message = 'This account has already been activated. If you need to reset your password, please <a href="#/forgot-password">click here</a> to reset your password.';
        }
        this.openBadModal(message);
      }
    );
  }

  loading(event: boolean) {
    this.isLoading.next(event);
  }

  ngOnDestroy() {
    this.destroyed.next(true);
    this.destroyed.complete();
  }

}
